import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ResourceService } from './resource-service';
import { ApplyRecommendationPayload, Recommendation, RecommendationEntityType, RecommendationType } from '../models/recommendation';
import { SortDescriptor } from '@progress/kendo-data-query';
import { Observable } from 'rxjs';
import { ItemPricingRecommendation } from '../models/item-pricing-recommendation';

interface IFilterItemPricingRecommendations {
  skip: number;
  take: number;
  filter: any;
  sort: SortDescriptor[];
  additionalOptions: {
    marketplaceId: string;
    currencyCode: string;
    entityType: RecommendationEntityType;
    type: RecommendationType;
  }
}

@Injectable({
  providedIn: 'root',
})
export class RecommendationsService extends ResourceService<Recommendation> {
  constructor(protected httpClient: HttpClient) {
    super(httpClient, '/api/recommendation');
  }

  getList(filter: IFilterItemPricingRecommendations): Observable<ItemPricingRecommendation[]> {
    return this.getFilteredUserTelerikEvent(filter.skip, filter.take, filter.filter, filter.sort, `/api/recommendation/list`, undefined, filter.additionalOptions) as unknown as Observable<ItemPricingRecommendation[]>;
  }

  apply(payload: ApplyRecommendationPayload) {
    return this.httpClient.post(`${this.apiUrl}/apply`, payload);
  }

  getRecommendationByEntityKey(entityKey: string): Observable<ItemPricingRecommendation> {
    return this.httpClient.get<ItemPricingRecommendation>(`${this.apiUrl}/${entityKey}`);
  }

  getCountByState(): Observable<Record<string, number>> {
    return this.httpClient.get<Record<string, number>>(`${this.apiUrl}/count-by-state`);
  }

  getPendingCsvUpload(): Observable<{ hasPending: boolean; uploadBatchId?: string; totalCount?: number }> {
    return this.httpClient.get<{ hasPending: boolean; uploadBatchId?: string; totalCount?: number }>(
      `${this.apiUrl}/pending-csv-upload`
    );
  }

  revertCsvUpload(uploadBatchId: string): Observable<{ success: boolean }> {
    return this.httpClient.post<{ success: boolean }>(`${this.apiUrl}/revert-csv-upload`, { uploadBatchId });
  }

  getBulkJobHistory(params?: {
    offset?: number;
    limit?: number;
    status?: string;
    statuses?: string[];
    isRevert?: boolean;
    actor?: string;
    statusFilters?: any;
    startDate?: string;
    endDate?: string;
  }): Observable<any> {
    const httpParams: Record<string, any> = {};
    if (params) {
      if (params.offset != null) httpParams['offset'] = params.offset;
      if (params.limit != null) httpParams['limit'] = params.limit;
      if (params.status && params.status !== 'ALL') httpParams['status'] = params.status;
      if (params.statuses?.length) httpParams['statuses'] = params.statuses.join(',');
      if (params.statusFilters?.length) httpParams['statusFilters'] = JSON.stringify(params.statusFilters);
      if (params.isRevert != null) httpParams['isRevert'] = params.isRevert;
      if (params.actor) httpParams['actor'] = params.actor;
      if (params.startDate) httpParams['startDate'] = params.startDate;
      if (params.endDate) httpParams['endDate'] = params.endDate;
    }
    return this.httpClient.get<any>(`${this.apiUrl}/bulk-history`, { params: httpParams });
  }

  getBulkJobById(jobBatchId: string): Observable<any> {
    return this.httpClient.get<any>(`${this.apiUrl}/bulk-history/${jobBatchId}`);
  }

  getBulkJobSnapshots(jobBatchId: string, limit?: number, offset?: number, search?: string, status?: string): Observable<any> {
    const httpParams: Record<string, any> = {};
    if (limit != null) httpParams['limit'] = limit;
    if (offset != null) httpParams['offset'] = offset;
    if (search) httpParams['search'] = search;
    if (status && status !== 'ALL') httpParams['status'] = status;
    return this.httpClient.get<any>(`${this.apiUrl}/bulk-history/${jobBatchId}/snapshots`, { params: httpParams });
  }

  revertBulkJob(jobBatchId: string): Observable<any> {
    return this.httpClient.post<any>(`${this.apiUrl}/bulk-history/${jobBatchId}/revert`, {});
  }
}
